import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useImageZoomGestures } from '@/hooks/use-image-zoom-gestures';
import type { IClientPhoto } from '@/entities/client.entity';
import { photoDownloadFileName, photoFileUrl } from '@/lib/client-photo-url';
import { downloadClientPhoto } from '@/lib/download-client-photo';
import { getPhotoMatchCategoryLabel, isPhotoMatchCategoryId } from '@crave/config/photo-match-categories';
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	DownloadIcon,
	RotateCcwIcon,
	XIcon,
	ZoomInIcon,
	ZoomOutIcon,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

interface ClientPhotoViewerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	clientId: string;
	photos: IClientPhoto[];
	activeIndex: number;
	onActiveIndexChange: (index: number) => void;
}

function categoryLabel(category: string): string {
	return isPhotoMatchCategoryId(category) ? getPhotoMatchCategoryLabel(category) : category;
}

function formatTakenAt(value: string | null): string | null {
	if (!value) return null;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	return date.toLocaleString('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	});
}

export default function ClientPhotoViewer({
	open,
	onOpenChange,
	clientId,
	photos,
	activeIndex,
	onActiveIndexChange,
}: ClientPhotoViewerProps) {
	const viewportRef = useRef<HTMLDivElement | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	const photo = photos[activeIndex] ?? null;
	const hasPrev = activeIndex > 0;
	const hasNext = activeIndex < photos.length - 1;

	const goPrev = useCallback(() => {
		if (hasPrev) onActiveIndexChange(activeIndex - 1);
	}, [activeIndex, hasPrev, onActiveIndexChange]);

	const goNext = useCallback(() => {
		if (hasNext) onActiveIndexChange(activeIndex + 1);
	}, [activeIndex, hasNext, onActiveIndexChange]);

	const { transform, reset, zoomIn, zoomOut, canZoomIn, canZoomOut } = useImageZoomGestures(viewportRef, {
		enabled: open && Boolean(photo),
		onSwipeLeft: goNext,
		onSwipeRight: goPrev,
	});

	useEffect(() => {
		reset();
	}, [activeIndex, open, reset]);

	useEffect(() => {
		if (!open) return;
		if (photos.length === 0) {
			onOpenChange(false);
			return;
		}
		if (activeIndex >= photos.length) {
			onActiveIndexChange(photos.length - 1);
		}
	}, [open, photos.length, activeIndex, onOpenChange, onActiveIndexChange]);

	useEffect(() => {
		if (!open) return;

		function onKeyDown(event: KeyboardEvent) {
			if (event.key === 'ArrowLeft') {
				event.preventDefault();
				goPrev();
			}
			if (event.key === 'ArrowRight') {
				event.preventDefault();
				goNext();
			}
			if (event.key === 'Escape') {
				onOpenChange(false);
			}
		}

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [open, goPrev, goNext, onOpenChange]);

	async function handleSave() {
		if (!photo || isSaving) return;
		setIsSaving(true);
		try {
			const url = photoFileUrl(clientId, photo.id, 'full');
			await downloadClientPhoto(url, photoDownloadFileName(photo.mediaId));
		} catch {
			toast.error('Не удалось сохранить фото');
		} finally {
			setIsSaving(false);
		}
	}

	if (!open) return null;

	const takenLabel = photo ? formatTakenAt(photo.takenAt) : null;
	const fullUrl = photo ? photoFileUrl(clientId, photo.id, 'full') : '';
	const metaLine = photo
		? [
				categoryLabel(photo.category),
				photos.length > 0 ? `${activeIndex + 1} / ${photos.length}` : null,
				takenLabel,
			]
				.filter(Boolean)
				.join(' · ')
		: 'Фото недоступно';

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className='fixed inset-0 top-0 left-0 translate-x-0 translate-y-0 max-w-none w-full h-[100dvh] rounded-none border-0 p-0 gap-0 bg-black/95 text-white flex flex-col overflow-hidden'
			>
				<DialogTitle className='sr-only'>
					{photo ? categoryLabel(photo.category) : 'Фото недоступно'}
				</DialogTitle>

				<div className='shrink-0 bg-black/40'>
					<div className='flex items-center gap-2 px-3 pt-2 pb-1'>
						<p className='min-w-0 flex-1 text-sm text-white/90 truncate'>{metaLine}</p>
						<Button
							type='button'
							variant='ghost'
							size='icon-sm'
							className='shrink-0 text-white hover:bg-white/10 hover:text-white'
							onClick={() => onOpenChange(false)}
							aria-label='Закрыть'
						>
							<XIcon />
						</Button>
					</div>
					<div className='flex items-center justify-end gap-1 px-3 pb-2 border-b border-white/10'>
						<Button
							type='button'
							variant='ghost'
							size='icon-sm'
							className='text-white hover:bg-white/10 hover:text-white'
							onClick={zoomOut}
							disabled={!canZoomOut}
							aria-label='Уменьшить'
						>
							<ZoomOutIcon />
						</Button>
						<Button
							type='button'
							variant='ghost'
							size='icon-sm'
							className='text-white hover:bg-white/10 hover:text-white'
							onClick={reset}
							disabled={!canZoomOut}
							aria-label='Сбросить масштаб'
						>
							<RotateCcwIcon />
						</Button>
						<Button
							type='button'
							variant='ghost'
							size='icon-sm'
							className='text-white hover:bg-white/10 hover:text-white'
							onClick={zoomIn}
							disabled={!canZoomIn}
							aria-label='Увеличить'
						>
							<ZoomInIcon />
						</Button>
						<Button
							type='button'
							variant='ghost'
							size='sm'
							className='text-white hover:bg-white/10 hover:text-white'
							onClick={handleSave}
							disabled={isSaving}
						>
							<DownloadIcon />
							Сохранить
						</Button>
					</div>
				</div>

				<div
					ref={viewportRef}
					className='relative flex-1 min-h-0 overflow-hidden touch-none select-none'
				>
					{photo ? (
						<img
							src={fullUrl}
							alt={categoryLabel(photo.category)}
							className='absolute top-1/2 left-1/2 max-h-full max-w-full object-contain will-change-transform'
							style={{
								transform: `translate(calc(-50% + ${transform.translateX}px), calc(-50% + ${transform.translateY}px)) scale(${transform.scale})`,
							}}
							draggable={false}
						/>
					) : (
						<div className='absolute inset-0 flex items-center justify-center text-sm text-white/70'>
							Фото недоступно
						</div>
					)}
				</div>

				<div className='flex items-center justify-between px-3 py-2 shrink-0 border-t border-white/10 bg-black/40'>
					<Button
						type='button'
						variant='ghost'
						size='sm'
						className='text-white hover:bg-white/10 hover:text-white'
						onClick={goPrev}
						disabled={!hasPrev}
					>
						<ChevronLeftIcon />
						Назад
					</Button>
					<Button
						type='button'
						variant='ghost'
						size='sm'
						className='text-white hover:bg-white/10 hover:text-white'
						onClick={goNext}
						disabled={!hasNext}
					>
						Далее
						<ChevronRightIcon />
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
